// Copyright 1999-2017. Plesk International GmbH. All rights reserved.

import { createElement, Component, cloneElement, isValidElement } from '../../utils';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { CLS_PREFIX } from '../../constants';
import Icon, { ICON_SIZE_16, ICON_SIZE_24, ICON_SIZE_32, ICON_SIZE_48, ICON_SIZE_64, ICON_SIZE_96, ICON_SIZE_128, ICON_SIZE_192 } from '../Icon';

import './Item.less';

/**
 * `Item` component.
 */
class Item extends Component {
    renderTitle(title) {
        const { baseClassName } = this.props;

        if (typeof title === 'string') {
            title = <span>{title}</span>;
        } else if (typeof title === 'object' && !isValidElement(title)) {
            title = <a {...title} />;
        }

        if (isValidElement(title)) {
            title = cloneElement(title, {
                className: classNames(
                    `${baseClassName}__title`,
                    title.props.className,
                ),
            });
        }

        return title;
    }

    renderIcon(icon) {
        if (typeof icon === 'string') {
            return Icon.create({}, {
                size: ICON_SIZE_32,
                src: icon,
            });
        }

        return Icon.create(icon, { size: ICON_SIZE_32 });
    }

    render() {
        const {
            children,
            baseClassName,
            component: Tag,
            className,
            icon,
            title,
            ...props
        } = this.props;
        const iconElement = this.renderIcon(icon);
        const map = {
            [ICON_SIZE_16]: 'sm',
            [ICON_SIZE_24]: 'md',
            [ICON_SIZE_32]: 'md',
            [ICON_SIZE_48]: 'lg',
            [ICON_SIZE_64]: 'lg',
            [ICON_SIZE_96]: 'lg',
            [ICON_SIZE_128]: 'lg',
            [ICON_SIZE_192]: 'lg',
        };
        const size = map[iconElement.props.size] || ICON_SIZE_32;

        return (
            <Tag
                className={classNames(baseClassName, `${baseClassName}--size-${size}`, className)}
                {...props}
            >
                <div className={`${baseClassName}__icon`}>
                    {iconElement}
                </div>
                <div className={`${baseClassName}__content`}>
                    {this.renderTitle(title)}
                    {children && (
                        <div className={`${baseClassName}__body`}>
                            {children}
                        </div>
                    )}
                </div>
            </Tag>
        );
    }
}

Item.propTypes = {
    /**
     * Component to render as the root element. Useful when rendering a `Item` as `<a>` or `<Link>`.
     */
    component: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.func,
    ]),
    /**
     * Icon
     */
    icon: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object,
        PropTypes.element,
    ]).isRequired,
    /**
     * Title
     */
    title: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object,
        PropTypes.element,
    ]),
    /**
     * Content of the `Item`.
     */
    children: PropTypes.node,
    /**
     * @ignore
     */
    className: PropTypes.string,
    /**
     * @ignore
     */
    baseClassName: PropTypes.string,
};
Item.defaultProps = {
    component: 'div',
    title: null,
    children: null,
    className: null,
    baseClassName: `${CLS_PREFIX}item`,
};

export default Item;



// WEBPACK FOOTER //
// ./components/Item/Item.js